/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  SyntheticEvent,
  useCallback,
  useRef,
  useState
} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {ColDef} from 'ag-grid-community';
import {getDeviceIdentifier} from '@ion/applications/src/pages/devices/ViewDeviceEditorUtils';
import {translate} from '@ion/core/src/locale/locale';
import {isSuccess} from '@ion/core/src/services/ServiceErrors';
import {Assets, Devices, GenericObject} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import {CSVExportButton} from '@ion/dls/src/components/buttons/CSVExportButton';
import {
  resolveDefinitions
} from '@ion/dls/src/grid/ColumnResolver';
import {
  IonGrid,
  RowModelTypes
} from '@ion/dls/src/grid/IonGrid';
import {
  GridResult
} from '@ion/dls/src/grid/PaginationControls';
import {
  PAGE_SIZES
} from '@ion/dls/src/grid/settings';
import {
  useAnimatedMount
} from '../hooks/useAnimatedMount';
import {
  useExtendStyles
} from '../hooks/useExtendStyles';
import {dlsColor} from '../theme/theme';
import {DLSTheme} from '../types/types';
import EditorDialog from './EditorDialog';
import styles from './editorStyles';
import {getResults} from './MovedEntitiesEditorUtils';

const styleOverrides = () => ({
  exportIcon : {
    display        : 'flex',
    justifyContent : 'end'
  }
});

const columnStyles = makeStyles(() => ({
  responseColor : {
    color : ({code}: {code?: number}) => !isSuccess(code) ? dlsColor('caRed') : dlsColor('caGreen')
  }
}));

const dialogOverrides = (theme: DLSTheme) => ({
  form : {
    height : '100%'
  },
  formPaper : {
    minWidth    : '70% !important',
    paddingLeft : '5px !important',
    height      : '521px',
    minHeight   : 'unset !important'
  },
  paperScrollPaper : {
    [theme.breakpoints.down('sm')] : {
      minHeight : '100%'
    }
  }
});

// defines the Moved Devices editor prop type definitions ...
interface MovedDevicesEditorProps {
  entity: string;
  isExporting: boolean;
  errors: ServiceError[];
  movedEntities: Devices | Assets;
  onCancel?: (event: SyntheticEvent<{}, Event>) => void;
  onMovedDevicesExportClick: (data: Devices | Assets) => void;
}

const getColorClass = (item) => {
  const classes = columnStyles({code: item?.code});
  return classes.responseColor;
};

/**
 * The Moved Entities Editor is to show the Status grid of Bulk moved entities.
 *
 * @param {string} entity Determines if the DeviceMoveEditor dialog should be open.
 * @param {ServiceError[]} errors Devices that got failed to move.
 * @param {Function} onCancel Callback function fired when the dialog is to be closed.
 * @param {Devices | Assets} movedEntities Devices that got successfully moved
 * @param {Function} onMovedDevicesExportClick Callback function fired when the export is clicked.
 * @param {boolean} isExporting If true export button will be disabled to show the progress.
 */
export const MovedEntitiesEditor = ({
  entity,
  errors,
  onCancel,
  isExporting,
  movedEntities,
  onMovedDevicesExportClick
}: MovedDevicesEditorProps) => {
  const editorStyles            = useExtendStyles(styles, styleOverrides);
  const classes                 = editorStyles();
  const ionGridRef              = useRef<GenericObject>();
  const {isVisible, onRemove}   = useAnimatedMount();
  const [gridApi, setGridApi]   = useState(null);
  const [hasItems, setHasItems] = useState(false);

  const onGridReady = useCallback((api) => {
    setGridApi(api);
  }, []);

  const onClose = useCallback(() => {
    onRemove(onCancel);
  }, [onCancel]);

  const onExport = useCallback(() => {
    const contents = ionGridRef.current?.getAllDisplayedRowData();
    onMovedDevicesExportClick(contents);
  }, []);

  const getMovedDevicesData = useCallback(({callback}) => {
    const items = getResults(entity, movedEntities, errors);
    callback({items: items});
    return items;
  }, [entity, errors, movedEntities]);

  /**
   * Converts a collection of Moved Devices and converts them to a list of items specific
   * for display within the Moved Devices Grid, while retaining all of the items existing
   * attributes,
  */
  const constructMovedDevicesGridRows = useCallback((data: GenericObject[]): GridResult[] => {
    const gridItems = data.map(entity => ({
      ...entity,
      name       : entity?.name || entity?.id,
      identifier : entity?.identifier || getDeviceIdentifier(entity),
      code       : entity?.code,
      response   : entity?.response || ''
    })) as GridResult[];
    setHasItems(!!gridItems.length);
    return gridItems;
  }, []);

  return (
    <EditorDialog open={isVisible}
      dialogTitle={translate('common:editors.movedEntities.title', {
        entity : entity
      })}
      onCloseDialog={onClose}
      styleOverrides={dialogOverrides} >
      <>
        {hasItems && (
          <div className={classes.exportIcon}>
            <CSVExportButton onExport={onExport} isExporting={isExporting} />
          </div>
        )}
        <div className={classes.gridContainer}>
          <IonGrid omitHeader
            pageOffset
            ref={ionGridRef}
            rowModelType={RowModelTypes.ClientSide}
            loadItems={getMovedDevicesData}
            augmentItems={constructMovedDevicesGridRows}
            columnDefinitions={getColumnDefinitions(entity)}
            checkboxesEnabled={true}
            showHeaderCheckbox={true}
            rowMenusEnabled={false}
            viewSubGroups={false}
            onGridReady={onGridReady}
            columnComponents={{
              response : (item) => (
                <span className={getColorClass(item?.data)}>{item?.data?.response}</span>
              )
            }}
            pageSizes={PAGE_SIZES} />
        </div>
      </>
    </EditorDialog>
  );
};

/**
 * Returns column definitions for desktop moved entities grid
 */
const getColumnDefinitionsDesktop = (entity: string): ColDef[] => ([{
  headerName : translate('common:editors.movedEntities.grid.name', {entity: entity}),
  field      : 'name',
  sortable   : false,
  filter     : false,
  resizable  : true,
  unSortIcon : false,
  hide       : false
},
{
  headerName : translate(`common:editors.movedEntities.grid.${entity === translate('common:entityLabels.asset') ? 'lastModified' : 'identifier'}`, {entity: entity}),
  field      : 'identifier',
  sortable   : false,
  filter     : false,
  resizable  : true,
  unSortIcon : false,
  hide       : false
},
{
  headerName : translate('common:editors.movedEntities.grid.code'),
  field      : 'code',
  sortable   : false,
  filter     : false,
  resizable  : true,
  unSortIcon : false,
  hide       : false
},
{
  headerName         : translate('common:editors.movedEntities.grid.response'),
  field              : 'response',
  cellRenderer       : 'response',
  cellRendererParams : ({data}) => ({data}),
  sortable           : false,
  filter             : false,
  resizable          : true,
  unSortIcon         : false,
  hide               : false
}]);

const getColumnDefinitions = (entity: string) => ({
  desktop : resolveDefinitions(getColumnDefinitionsDesktop(entity)),
  mobile  : resolveDefinitions(getColumnDefinitionsDesktop(entity))
});

export default MovedEntitiesEditor;
